export type PausedActionTooltipInput = {
  isPaused: boolean;
  isCompleted: boolean;
  isRecordingStatus: boolean;
  isProcessingStatus: boolean;
  canCompleteFromFailedOrCancelled: boolean;
  isFailedOrCancelledAwaitingChunks: boolean;
  isWidgetActive: boolean;
  isLocallyPausedWidget: boolean;
  isResuming: boolean;
  isResumePleaseWait: boolean;
  isCompleting: boolean;
};

export const RESUME_TOOLTIP_PLEASE_WAIT = "Please wait…";
export const RESUME_TOOLTIP_WIDGET_BLOCKING =
  "Stop the current recording before resuming";
export const RESUME_TOOLTIP_RECORDING_STATUS =
  "Use Postpone on the recording widget to save for later, or use Complete Scribe when you are finished";
export const RESUME_TOOLTIP_WRONG_STATUS =
  "Resume is only available for paused or completed scribes";
export const RESUME_TOOLTIP_ENABLED =
  "Continue recording more audio for this scribe";

export const COMPLETE_TOOLTIP_PLEASE_WAIT = "Please wait…";
export const COMPLETE_TOOLTIP_ALREADY_COMPLETED =
  "This scribe is already completed";
export const COMPLETE_TOOLTIP_UNAVAILABLE =
  "Available when the scribe is paused, processing, failed or cancelled with transcribed audio, or while recording is in progress";
export const COMPLETE_TOOLTIP_LOADING_AUDIO =
  "Loading audio recordings…";
export const COMPLETE_TOOLTIP_WIDGET_ACTIVE =
  "Stop recording and generate your Scribe note and summary";
export const COMPLETE_TOOLTIP_RECORDING_STATUS =
  "Generate your Scribe note and summary from the current recording";
export const COMPLETE_TOOLTIP_PROCESSING =
  "Generate or retry your Scribe note and summary";
export const COMPLETE_TOOLTIP_FAILED_OR_CANCELLED =
  "Regenerate your Scribe note and summary from saved audio";
export const COMPLETE_TOOLTIP_PAUSED =
  "Generate your Scribe note and summary from the saved audio";

export function isCompleteButtonDisabled(
  input: PausedActionTooltipInput
): boolean {
  const {
    isPaused,
    isWidgetActive,
    isRecordingStatus,
    isProcessingStatus,
    canCompleteFromFailedOrCancelled,
    isCompleting
  } = input;
  return (
    !(
      isPaused ||
      isWidgetActive ||
      isRecordingStatus ||
      isProcessingStatus ||
      canCompleteFromFailedOrCancelled
    ) || isCompleting
  );
}

export function isResumeButtonPleaseWait(
  input: PausedActionTooltipInput
): boolean {
  return input.isResumePleaseWait;
}

export function isResumeButtonDisabled(
  input: PausedActionTooltipInput
): boolean {
  const {
    isPaused,
    isCompleted,
    isWidgetActive,
    isLocallyPausedWidget,
    isResumePleaseWait
  } = input;
  const canResume = isPaused || isCompleted || isLocallyPausedWidget;
  const blockedByActiveWidget = isWidgetActive && !isLocallyPausedWidget;
  return !canResume || blockedByActiveWidget || isResumePleaseWait;
}

export function getResumeTooltipTitle(input: PausedActionTooltipInput): string {
  const {
    isPaused,
    isCompleted,
    isRecordingStatus,
    isWidgetActive,
    isLocallyPausedWidget,
    isResumePleaseWait
  } = input;

  if (isResumePleaseWait) {
    return RESUME_TOOLTIP_PLEASE_WAIT;
  }
  if (isLocallyPausedWidget) {
    return RESUME_TOOLTIP_ENABLED;
  }
  if (isWidgetActive) {
    return RESUME_TOOLTIP_WIDGET_BLOCKING;
  }
  if (!(isPaused || isCompleted)) {
    if (isRecordingStatus) {
      return RESUME_TOOLTIP_RECORDING_STATUS;
    }
    return RESUME_TOOLTIP_WRONG_STATUS;
  }
  return RESUME_TOOLTIP_ENABLED;
}

export function getCompleteTooltipTitle(
  input: PausedActionTooltipInput
): string {
  const {
    isPaused,
    isCompleted,
    isRecordingStatus,
    isProcessingStatus,
    canCompleteFromFailedOrCancelled,
    isFailedOrCancelledAwaitingChunks,
    isWidgetActive,
    isCompleting
  } = input;

  if (isCompleting) {
    return COMPLETE_TOOLTIP_PLEASE_WAIT;
  }

  if (isCompleteButtonDisabled(input)) {
    if (isCompleted) {
      return COMPLETE_TOOLTIP_ALREADY_COMPLETED;
    }
    if (isFailedOrCancelledAwaitingChunks) {
      return COMPLETE_TOOLTIP_LOADING_AUDIO;
    }
    return COMPLETE_TOOLTIP_UNAVAILABLE;
  }
  if (isWidgetActive) {
    return COMPLETE_TOOLTIP_WIDGET_ACTIVE;
  }
  if (isRecordingStatus) {
    return COMPLETE_TOOLTIP_RECORDING_STATUS;
  }
  if (isProcessingStatus) {
    return COMPLETE_TOOLTIP_PROCESSING;
  }
  if (canCompleteFromFailedOrCancelled) {
    return COMPLETE_TOOLTIP_FAILED_OR_CANCELLED;
  }
  if (isPaused) {
    return COMPLETE_TOOLTIP_PAUSED;
  }
  return COMPLETE_TOOLTIP_UNAVAILABLE;
}
